import React from 'react'

interface FieldMappingsSideNavProps {
  categories: string[]
  activeCategory: string
  setActiveCategory: (category: string) => void
  mappingStats: { [category: string]: { mapped: number; total: number } }
}

const FieldMappingsSideNav: React.FC<FieldMappingsSideNavProps> = ({
  categories,
  activeCategory,
  setActiveCategory,
  mappingStats
}) => {
  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'Business':
        return '🏢'
      case 'Business Address':
        return '📍'
      case 'Primary Contact':
        return '👤'
      case 'Contact Address':
        return '📮'
      case 'Deal Information':
        return '💰'
      case 'General':
        return '📋'
      default:
        return '📄'
    }
  }

  const getCompletionPercentage = (category: string): number => {
    const stats = mappingStats[category]
    if (!stats || stats.total === 0) return 0
    return Math.round((stats.mapped / stats.total) * 100)
  }

  const getCompletionStatus = (percentage: number): string => {
    if (percentage === 100) return 'complete'
    if (percentage >= 75) return 'mostly-complete'
    if (percentage >= 50) return 'half-complete'
    if (percentage > 0) return 'partial'
    return 'empty'
  }

  return (
    <div className="field-mappings-sidenav">
      <div className="sidenav-header">
        <h3>Field Categories</h3>
        <p className="sidenav-description">
          Configure mappings for each section of the application form
        </p>
      </div>

      <nav className="sidenav-nav">
        {categories.map((category) => {
          const stats = mappingStats[category]
          const percentage = getCompletionPercentage(category)
          const status = getCompletionStatus(percentage)

          return (
            <button
              key={category}
              className={`sidenav-item ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              <div className="sidenav-item-content">
                <div className="sidenav-item-header">
                  <span className="category-icon">{getCategoryIcon(category)}</span>
                  <span className="category-name">{category}</span>
                </div>
                
                <div className="sidenav-item-stats">
                  <div className="field-count">
                    {stats ? `${stats.mapped}/${stats.total}` : '0/0'} mapped
                  </div>
                  
                  <div className={`completion-badge ${status}`}>
                    {percentage}%
                  </div>
                </div>

                <div className="progress-bar">
                  <div 
                    className={`progress-fill ${status}`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            </button>
          )
        })}
      </nav>

      <div className="sidenav-footer">
        <div className="mapping-summary">
          <h4>Overall Progress</h4>
          {(() => {
            const totalMapped = Object.values(mappingStats).reduce((sum, stats) => sum + stats.mapped, 0)
            const totalFields = Object.values(mappingStats).reduce((sum, stats) => sum + stats.total, 0)
            const overallPercentage = totalFields > 0 ? Math.round((totalMapped / totalFields) * 100) : 0
            
            return (
              <div className="overall-stats">
                <div className="overall-count">
                  {totalMapped} of {totalFields} fields mapped
                </div>
                <div className="overall-percentage">
                  {overallPercentage}% complete
                </div>
                <div className="progress-bar large">
                  <div 
                    className={`progress-fill ${getCompletionStatus(overallPercentage)}`}
                    style={{ width: `${overallPercentage}%` }}
                  ></div>
                </div>
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

export default FieldMappingsSideNav
